import { useState, useEffect } from "react";
import { X, Plus, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { scaleFromWeekly } from "@/lib/rates";
import { PhotoUploader } from "@/components/PhotoUploader";

const In = ({ label, v, on, t, type = "text", req }) => (
  <label className="block">
    <span className="label-caps text-slate-400 block mb-2">{label}</span>
    <input type={type} value={v} onChange={on} data-testid={t} required={req} className="belo-input" />
  </label>
);

const Sel = ({ label, v, on, t, opts }) => (
  <label className="block">
    <span className="label-caps text-slate-400 block mb-2">{label}</span>
    <select value={v} onChange={on} data-testid={t} className="belo-input">
      {opts.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </label>
);

export function CarModal({ data, onClose, onSave }) {
  const [form, setForm] = useState(data);
  const [hosts, setHosts] = useState([]);
  const [presets, setPresets] = useState({});
  const [transferTo, setTransferTo] = useState(data.host_id || "");
  const [transferring, setTransferring] = useState(false);
  const initR = data.rates || {};
  const initT = initR.tiers || {};
  const [rateCat, setRateCat] = useState(initR.category || data.category || "Economy");
  const [tiers, setTiers] = useState({
    2: initT[2] ?? initT["2"] ?? "", 3: initT[3] ?? initT["3"] ?? "", 4: initT[4] ?? initT["4"] ?? "",
    5: initT[5] ?? initT["5"] ?? "", 6: initT[6] ?? initT["6"] ?? "", 7: initT[7] ?? initT["7"] ?? "",
  });
  const [extraDay, setExtraDay] = useState(initR.extra_day ?? "");
  const [weekly, setWeekly] = useState(initT[7] ?? initT["7"] ?? data.weekly_rate ?? "");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  useEffect(() => {
    api.get("/admin/users", { params: { hosts_only: true } }).then((r) => setHosts(r.data || [])).catch(() => {});
    api.get("/config").then((r) => setPresets(r.data.category_rates || {})).catch(() => {});
  }, []);
  const setCategory = (e) => {
    const cat = e.target.value;
    setForm((f) => ({ ...f, category: cat }));
    const p = presets[cat];
    if (p) { setRateCat(cat); setTiers({ ...p.tiers }); setExtraDay(p.extra_day); setWeekly(p.tiers["7"] ?? p.tiers[7]); }
  };
  const applyWeekly = (val) => {
    setWeekly(val);
    const s = scaleFromWeekly(presets[rateCat] || presets[form.category], val);
    if (s) { setTiers(s.tiers); setExtraDay(s.extra_day); }
  };
  const submit = () => {
    const payload = {
      ...form,
      weekly_rate: Number(tiers[7]) || Number(form.weekly_rate) || 0,
      rate_schedule: {
        category: rateCat,
        tiers: Object.fromEntries([2, 3, 4, 5, 6, 7].map((d) => [String(d), Number(tiers[d]) || 0])),
        extra_day: Number(extraDay) || 0,
        daily_start: (presets[rateCat]?.daily_start) || Math.round((Number(tiers[7]) || 0) / 7),
      },
    };
    onSave(payload);
  };
  const doTransfer = async () => {
    setTransferring(true);
    try {
      await api.put(`/admin/cars/${form.id}/transfer`, { host_id: transferTo || null });
      toast.success(transferTo ? "Listing transferred to host." : "Listing moved back to Belo fleet.");
      onClose();
      window.location.reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setTransferring(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="car-modal">
      <div className="bg-white rounded-2xl shadow-card-hover w-full max-w-2xl my-8">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h2 className="font-heading text-xl font-semibold">{form.id ? "Edit Car" : "Add Car"}</h2>
          <button onClick={onClose} data-testid="modal-close" className="text-slate-400 hover:text-slate-900"><X size={20} /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto">
          <In label="Name" v={form.name} on={set("name")} t="car-form-name" req />
          <In label="Make" v={form.make} on={set("make")} t="car-form-make" req />
          <Sel label="Category" v={form.category} on={setCategory} t="car-form-category" opts={["Economy", "Sedan", "SUV", "Truck", "Pickup Truck", "Minivan", "Cargo Van", "Luxury", "Electric", "Sports"]} />
          <In label="Deposit ($)" type="number" v={form.deposit} on={set("deposit")} t="car-form-deposit" />
          <div className="sm:col-span-2 rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <span className="label-caps text-slate-500">Rental rates</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Weekly $</span>
                <input type="number" value={weekly} onChange={(e) => applyWeekly(e.target.value)} data-testid="car-form-weekly_rate" className="belo-input py-2 w-28" />
              </div>
            </div>
            <p className="text-xs text-slate-400">Set the weekly price to auto-calculate the daily tiers, then fine-tune any tier below. Prices scale from the vehicle's category shape.</p>
            <div className="grid grid-cols-4 gap-2.5">
              {[2, 3, 4, 5, 6, 7].map((d) => (
                <div key={d}>
                  <label className="text-xs text-slate-500">{d === 7 ? "1 week" : `${d} days`}</label>
                  <input type="number" value={tiers[d]} onChange={(e) => { const v = e.target.value; setTiers((p) => ({ ...p, [d]: v })); if (d === 7) setWeekly(v); }} data-testid={`car-tier-${d}`} className="belo-input w-full mt-1 py-2" />
                </div>
              ))}
              <div>
                <label className="text-xs text-slate-500">Extra day</label>
                <input type="number" value={extraDay} onChange={(e) => setExtraDay(e.target.value)} data-testid="car-tier-extra" className="belo-input w-full mt-1 py-2" />
              </div>
            </div>
          </div>
          <In label="Seats" type="number" v={form.seats} on={set("seats")} t="car-form-seats" req />
          <Sel label="Transmission" v={form.transmission} on={set("transmission")} t="car-form-transmission" opts={["Automatic", "Manual"]} />
          <Sel label="Fuel" v={form.fuel} on={set("fuel")} t="car-form-fuel" opts={["Gasoline", "Diesel", "Hybrid", "Electric"]} />
          <In label="Year" type="number" v={form.year ?? ""} on={set("year")} t="car-form-year" />
          <In label="Trim" v={form.trim ?? ""} on={set("trim")} t="car-form-trim" />
          <In label="Mileage (mi)" type="number" v={form.odometer ?? ""} on={set("odometer")} t="car-form-odometer" />
          <In label="VIN" v={form.vin ?? ""} on={(e) => { const vin = e.target.value.toUpperCase(); setForm((f) => ({ ...f, vin, stock_number: (!f.stock_number || f.stock_number === (f.vin || "").slice(-4)) && vin.length >= 4 ? vin.slice(-4) : f.stock_number })); }} t="car-form-vin" />
          <In label="Stock #" v={form.stock_number ?? ""} on={set("stock_number")} t="car-form-stock" />
          <Sel label="Pickup location" v={form.pickup_location ?? ""} on={set("pickup_location")} t="car-form-pickup" opts={["", "235 Washington St, Malden, MA 02148", "291 Berlin St, Clinton, MA 01510", "107 Union St, Leominster, MA 01453"]} />
          <div className="sm:col-span-2">
            <span className="label-caps text-slate-400 block mb-2">Photos</span>
            <PhotoUploader
              value={Array.isArray(form.images) ? form.images : (form.images ? String(form.images).split("\n").map((s) => s.trim()).filter(Boolean) : [])}
              onChange={(arr) => setForm((f) => ({ ...f, images: arr, image_url: arr[0] || f.image_url || "" }))}
              min={1}
            />
          </div>
          <div className="sm:col-span-2"><In label="Cover Image URL (optional override)" v={form.image_url} on={set("image_url")} t="car-form-image_url" /></div>
          <div className="sm:col-span-2">
            <span className="label-caps text-slate-400 block mb-2">Description</span>
            <textarea value={form.description} onChange={set("description")} data-testid="car-form-description" rows={2} className="belo-input resize-none" />
          </div>
          <div className="sm:col-span-2"><In label="Features (comma separated)" v={form.features} on={set("features")} t="car-form-features" /></div>
          {form.id && (
            <div className="sm:col-span-2 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <span className="label-caps text-slate-500 block mb-2">Ownership — transfer to host</span>
              <div className="flex flex-col sm:flex-row gap-2">
                <select value={transferTo} onChange={(e) => setTransferTo(e.target.value)} data-testid="car-transfer-host" className="belo-input flex-1">
                  <option value="">Belo fleet (no host)</option>
                  {hosts.map((h) => <option key={h.id} value={h.id}>{h.name || h.email} ({h.email})</option>)}
                </select>
                <button type="button" onClick={doTransfer} disabled={transferring || transferTo === (form.host_id || "")} data-testid="car-transfer-btn" className="rounded-full bg-slate-900 text-white px-5 py-2.5 font-semibold text-sm hover:bg-slate-800 disabled:opacity-40 whitespace-nowrap">{transferring ? "Transferring…" : "Transfer"}</button>
              </div>
              <p className="text-xs text-slate-400 mt-2">Reassigns this listing to a host account (host keeps 100% of payouts). Choose "Belo fleet" to bring it back in-house.</p>
            </div>
          )}
          <div className="sm:col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="border-2 border-slate-200 px-6 py-2.5 rounded-full font-semibold text-slate-700 hover:bg-slate-50 transition-colors">Cancel</button>
            <button type="submit" data-testid="car-form-save" className="bg-blue-600 text-white px-8 py-2.5 rounded-full font-semibold hover:bg-blue-700 transition-colors">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function DiscountModal({ data, onClose, onSave }) {
  const [form, setForm] = useState(data);
  const isEdit = !!form.id;
  const types = [["amount_25", "$25 Off"], ["amount_50", "$50 Off"], ["waive_young_driver", "Waive Young Driver Fee"]];
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="discount-modal">
      <div className="bg-white rounded-2xl shadow-card-hover w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h2 className="font-heading text-xl font-semibold">{isEdit ? "Edit Code" : "New Discount Code"}</h2>
          <button onClick={onClose} data-testid="discount-modal-close" className="text-slate-400 hover:text-slate-900"><X size={20} /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="p-6 space-y-5">
          <div>
            <span className="label-caps text-slate-400 block mb-2">Code</span>
            <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} disabled={isEdit} data-testid="discount-form-code" required className="belo-input font-mono uppercase disabled:bg-slate-100 disabled:text-slate-400" placeholder="SAVE25" />
          </div>
          <div>
            <span className="label-caps text-slate-400 block mb-2">Effect</span>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="discount-form-type" className="belo-input">
              {types.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <span className="label-caps text-slate-400 block mb-2">Max Uses (blank = unlimited)</span>
            <input type="number" min="1" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} data-testid="discount-form-maxuses" className="belo-input" placeholder="Unlimited" />
          </div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="discount-form-active" className="w-5 h-5 accent-blue-600" />
            <span className="text-slate-700 font-medium text-sm">Active</span>
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="border-2 border-slate-200 px-6 py-2.5 rounded-full font-semibold text-slate-700 hover:bg-slate-50 transition-colors">Cancel</button>
            <button type="submit" data-testid="discount-form-save" className="bg-blue-600 text-white px-8 py-2.5 rounded-full font-semibold hover:bg-blue-700 transition-colors">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function TuroImportModal({ onClose, onMutate }) {
  const [text, setText] = useState("");
  const [parsing, setParsing] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({ car_id: "", start: "", end: "", reason: "" });
  const [saving, setSaving] = useState(false);

  const detect = async () => {
    if (!text.trim()) { toast.error("Paste the Turo email first"); return; }
    setParsing(true);
    try {
      const res = await api.post("/admin/turo/parse", { text });
      setResult(res.data);
      setForm({
        car_id: res.data.matched_car_id || (res.data.cars[0]?.id || ""),
        start: res.data.start || "",
        end: res.data.end || "",
        reason: res.data.guest ? `Turo: ${res.data.guest}` : "Turo booking",
      });
      if (!res.data.matched_car_id) toast.message("Couldn't auto-match the car — please pick it below.");
      else toast.success(`Detected: ${res.data.matched_car_name}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setParsing(false); }
  };

  const block = async () => {
    if (!form.car_id || !form.start || !form.end) { toast.error("Car and both dates are required"); return; }
    setSaving(true);
    try {
      await api.post(`/admin/cars/${form.car_id}/blocks`, { start: form.start, end: form.end, reason: form.reason });
      toast.success("Turo booking blocked on your site");
      onMutate();
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="turo-import-modal">
      <div className="bg-white rounded-2xl shadow-card-hover w-full max-w-xl my-8">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <div>
            <h2 className="font-heading text-xl font-semibold">Import Turo Booking</h2>
            <p className="text-slate-500 text-sm mt-0.5">Paste your Turo "trip is booked" email — we'll detect the car and dates and block them.</p>
          </div>
          <button onClick={onClose} data-testid="turo-modal-close" className="text-slate-400 hover:text-slate-900"><X size={20} /></button>
        </div>

        <div className="p-6">
          <span className="label-caps text-slate-400 block mb-2">Turo email text</span>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            data-testid="turo-text"
            rows={6}
            className="belo-input resize-none"
            placeholder="e.g. Cha-ching! Miko's trip with your FIAT 500L at Boston Logan International Airport is booked from Thursday, July 2, 2026, 2:00PM to Sunday, July 5, 2026, 5:00AM..."
          />
          <button onClick={detect} disabled={parsing} data-testid="turo-detect" className="mt-3 w-full flex items-center justify-center gap-2 bg-slate-900 text-white py-2.5 rounded-full font-semibold hover:bg-slate-800 transition-colors disabled:opacity-50">
            {parsing ? "Detecting…" : "Detect Car & Dates"}
          </button>

          {result && (
            <div className="mt-6 border-t border-slate-100 pt-6" data-testid="turo-result">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block sm:col-span-2">
                  <span className="label-caps text-slate-400 block mb-2">Vehicle</span>
                  <select value={form.car_id} onChange={(e) => setForm({ ...form, car_id: e.target.value })} data-testid="turo-car" className="belo-input">
                    <option value="">Select a car…</option>
                    {result.cars.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="label-caps text-slate-400 block mb-2">From</span>
                  <input type="date" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} data-testid="turo-start" className="belo-input" />
                </label>
                <label className="block">
                  <span className="label-caps text-slate-400 block mb-2">To</span>
                  <input type="date" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} data-testid="turo-end" className="belo-input" />
                </label>
                <label className="block sm:col-span-2">
                  <span className="label-caps text-slate-400 block mb-2">Reason</span>
                  <input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="turo-reason" className="belo-input" />
                </label>
              </div>
              <button onClick={block} disabled={saving} data-testid="turo-block" className="mt-4 w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-2.5 rounded-full font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50">
                <Plus size={16} /> {saving ? "Blocking…" : "Block These Dates"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function AvailabilityModal({ car, onClose, onMutate }) {
  const [blocks, setBlocks] = useState(car.blocks || []);
  const [form, setForm] = useState({ start: "", end: "", reason: "" });
  const [saving, setSaving] = useState(false);

  const addBlock = async (e) => {
    e.preventDefault();
    if (!form.start || !form.end) return;
    setSaving(true);
    try {
      const res = await api.post(`/admin/cars/${car.id}/blocks`, form);
      setBlocks(res.data.blocks || []);
      setForm({ start: "", end: "", reason: "" });
      toast.success("Dates blocked");
      onMutate();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const removeBlock = async (bid) => {
    try {
      const res = await api.delete(`/admin/cars/${car.id}/blocks/${bid}`);
      setBlocks(res.data.blocks || []);
      toast.success("Block removed");
      onMutate();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="availability-modal">
      <div className="bg-white rounded-2xl shadow-card-hover w-full max-w-lg my-8">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <div>
            <h2 className="font-heading text-xl font-semibold">Manage Availability</h2>
            <p className="text-slate-500 text-sm mt-0.5">{car.name}</p>
          </div>
          <button onClick={onClose} data-testid="avail-modal-close" className="text-slate-400 hover:text-slate-900"><X size={20} /></button>
        </div>

        <div className="p-6">
          <p className="text-slate-600 text-sm">Block out dates when the car is unavailable (e.g. rented on Turo, maintenance). Customers won't be able to book overlapping dates.</p>

          <form onSubmit={addBlock} className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="label-caps text-slate-400 block mb-2">From</span>
              <input type="date" required value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} data-testid="block-start" className="belo-input" />
            </label>
            <label className="block">
              <span className="label-caps text-slate-400 block mb-2">To</span>
              <input type="date" required min={form.start} value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} data-testid="block-end" className="belo-input" />
            </label>
            <div className="sm:col-span-2">
              <span className="label-caps text-slate-400 block mb-2">Reason (optional)</span>
              <input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="block-reason" className="belo-input" placeholder="e.g. Booked on Turo" />
            </div>
            <div className="sm:col-span-2">
              <button type="submit" disabled={saving} data-testid="block-add" className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-2.5 rounded-full font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50">
                <Plus size={16} /> {saving ? "Adding…" : "Block Dates"}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <p className="label-caps text-slate-400 mb-3">Blocked Periods</p>
            {blocks.length === 0 ? (
              <p data-testid="no-blocks" className="text-slate-400 text-sm">No blocked dates. The car is bookable for all open dates.</p>
            ) : (
              <div className="space-y-2">
                {blocks.map((b) => (
                  <div key={b.id} data-testid={`block-${b.id}`} className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3">
                    <div>
                      <p className="text-slate-900 font-medium text-sm">{b.start} → {b.end}</p>
                      {b.reason && <p className="text-slate-500 text-xs">{b.reason}</p>}
                    </div>
                    <button onClick={() => removeBlock(b.id)} data-testid={`block-remove-${b.id}`} className="text-red-500 hover:text-red-700 p-1"><Trash2 size={16} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
